// @flow
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { type docsResultState } from "data/reducers/types";
import { type DocListLayout } from "data/types";
import CellMeasurerCache from "react-virtualized/dist/commonjs/CellMeasurer/CellMeasurerCache";
import Measure from "react-measure";
import Constants, { type ConstantsEnum } from "data/constants";
import NoDocs from "components/NoDocs";
import ListGroup from "react-bootstrap/lib/ListGroup";
import List from "react-virtualized/dist/commonjs/List";
import InfiniteLoader from "react-virtualized/dist/commonjs/InfiniteLoader";
import "react-virtualized/styles.css";
import RowResult from "./RowResult";
import isEqual from "lodash/isEqual";
import RowRenderer from "./RowRenderer";

type Props = {
    /** doclist fetch result */
    result: docsResultState,
    /** total number of documents */
    totalCount: number,
    /** callback to request loading next page */
    loadMoreRows: ({ rowIndex: number }) => Promise,
    /** docList layout settings */
    layout?: DocListLayout,
    /** callback to set filtering on a specific column
     * @param {string} colName name of column to filter
     */
    requestShowFilter: (colName: string) => void,
    /** which row index is currently selected */
    activeRowIndex?: ?number,
    /** which row index to bring into view */
    scrollToRow?: ?number,
    /** which row is the UI currently updating
     * @param {number} rowStartIndex row index in result.rows
     */
    rowStartIndexUpdated: (rowStartIndex: number) => void,
    /** callback when user clicks document icon
     * @param {SyntheticMouseEvent} e HTML click event
     */
    onIconClick?: (e: SyntheticMouseEvent) => void,
    /** callback when user single clicks the whole row
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} rowIndex the row's index
     */
    onSingleClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        rowIndex: number
    ) => void,
    /** callback when user clicks on More... button
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onMoreClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,
    /** whether to disable the More... button */
    actionsDisabled?: boolean,
    /** callback when user right clicks the whole row
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {number} rowIndex the row's index
     * @param {number} columnsIndex the column's index
     * @param {string} itemUri the row's itemUri
     */
    onContextClick?: (
        e: SyntheticMouseEvent,
        rowIndex: number,
        columnIndex: number,
        itemUri: string
    ) => void,
    /** React.Node children */
    children?: Node,
    /** whether to show thumbnails or just file types' icons */
    showThumbnails?: boolean,
    /** minimum row height (in px) */
    minRowHeight?: number,
    /** alternative Component used to Render each Row */
    RenderRowComponentClass?: React.Node,
};

type State = {
    rows: Object,
    pageSize: number,
    pagesStatus: Array<ConstantsEnum>,
    activeRowIndex: number,
    hoveredRowIndex: number,
    refreshId: number,
};

/**
 * Renders the Tiles/Thumbnails documents list
 */

const DocListSimple: React.FC<Props> = ({
    result,
    totalCount,
    loadMoreRows,
    layout,
    requestShowFilter,
    activeRowIndex: activeRowIndexFromProps = -1,
    scrollToRow,
    onIconClick,
    onSingleClick,
    onDoubleClick,
    onMoreClick,
    actionsDisabled,
    onContextClick,
    pageNum,
    children,
    showThumbnails = false,
    minRowHeight,
    RenderRowComponentClass = RowResult,
}) => {
    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            minHeight: minRowHeight || (showThumbnails ? 85 : 55),
        })
    );

    const [rows, setRows] = useState(result.rows);
    const [pageSize, setPageSize] = useState(result.pageSize);
    const [pagesStatus, setPagesStatus] = useState([Constants.OK]);
    const [activeRowIndex, setActiveRowIndex] = useState(
        activeRowIndexFromProps
    );
    const [hoveredRowIndex, setHoveredRowIndex] = useState(-1);
    const [refreshId, setRefreshId] = useState(0);

    const listRef = useRef(null);

    useEffect(() => {
        setActiveRowIndex(activeRowIndexFromProps);
    }, [activeRowIndexFromProps]);

    useEffect(() => {
        /* istanbul ignore else */
        if (listRef.current) {
            listRef.current.recomputeRowHeights();
        }
    }, [showThumbnails, minRowHeight]);

    useEffect(() => {
        /* istanbul ignore else */
        if (!isEqual(result.rows, rows)) {
            setRows(result.rows);
            setPagesStatus((pagesStatus) =>
                pagesStatus.map((p, i) =>
                    p === Constants.LOADING
                        ? i === pageNum
                            ? Constants.OK
                            : null
                        : p
                )
            );
            setRefreshId((refreshId) => refreshId + 1);
            cache.current.clearAll();

            /* istanbul ignore else */
            if (listRef.current) {
                listRef.current.recomputeRowHeights();
            }
        }
    }, [result.rows, pageNum]);

    const onResize = useCallback(() => {
        setRefreshId((refreshId) => refreshId + 1);
        cache.current.clearAll();
    }, []);

    const isRowLoaded = useCallback(
        ({ index }) => Object.prototype.hasOwnProperty.call(rows, index),
        [rows]
    );

    const loadMoreRowsCallback = useCallback(
        ({ startIndex, stopIndex }) => {
            let pageNum = 0;
            /* istanbul ignore else */
            if (startIndex > 0) {
                pageNum = Math.floor(startIndex / (pageSize || Infinity)) - 1;
            }
            /* istanbul ignore else */
            if (pageSize > 0 && (pageNum + 1) * pageSize < stopIndex) {
                pageNum++;
            }

            /* istanbul ignore next */
            if (
                (pagesStatus[pageNum] === Constants.OK ||
                    pagesStatus[pageNum] === Constants.LOADING) &&
                !(
                    Object.prototype.hasOwnProperty.call(rows, startIndex) &&
                    Object.prototype.hasOwnProperty.call(rows, stopIndex)
                )
            )
                return;

            setPagesStatus((prevStatus) => {
                // use immer for immutable state
                const newStatus = [...prevStatus];
                newStatus[pageNum] = Constants.LOADING;
                return newStatus;
            });
            loadMoreRows(pageNum);
        },
        [pageSize, pagesStatus, rows, loadMoreRows]
    );

    const cellOnContextMenu = useCallback(
        (e, rowIndex, itemUri) => {
            setActiveRowIndex(rowIndex);
            e.preventDefault();
            e.persist();
            onContextClick?.(e, rowIndex, 0, itemUri);
        },
        [onContextClick]
    );

    const count = totalCount === 0 ? result.rowCount : totalCount;

    const rowRenderer = useCallback(
        (params) => (
            <RowRenderer
                {...params}
                isRowLoaded={isRowLoaded}
                rows={rows}
                cache={cache.current}
                refreshId={refreshId}
                activeRowIndex={activeRowIndex}
                hoveredRowIndex={hoveredRowIndex}
                setHoveredRowIndex={setHoveredRowIndex}
                onResize={onResize}
                RenderRowComponentClass={RenderRowComponentClass}
                cellOnContextMenu={cellOnContextMenu}
                showThumbnails={showThumbnails}
                filterText={result.filterText}
                layout={layout}
                cols={result.cols}
                onIconClick={onIconClick}
                onSingleClick={onSingleClick}
                onMoreClick={onMoreClick}
                onDoubleClick={onDoubleClick}
                actionsDisabled={actionsDisabled}
            />
        ),
        [
            isRowLoaded,
            rows,
            refreshId,
            activeRowIndex,
            hoveredRowIndex,
            setHoveredRowIndex,
            onResize,
            RenderRowComponentClass,
            cellOnContextMenu,
            showThumbnails,
            result.filterText,
            layout,
            result.cols,
            onIconClick,
            onSingleClick,
            onMoreClick,
            actionsDisabled,
        ]
    );

    return (
        <Measure bounds onResize={onResize}>
            {({
                measureRef,
                contentRect: { bounds } = { bounds: { width: 0, height: 0 } },
            }) => (
                <div
                    ref={measureRef}
                    className="docListSimple"
                    style={{
                        flex: "1 1 100%",
                        display: "flex",
                        minHeight: minRowHeight
                            ? (minRowHeight + 3) * count
                            : 0,
                        minWidth: 0,
                    }}
                >
                    <ListGroup
                        style={{
                            marginBottom: 0,
                            flex: "1 1 100%",
                            minHeight: 0,
                            minWidth: 0,
                        }}
                    >
                        <InfiniteLoader
                            isRowLoaded={isRowLoaded}
                            loadMoreRows={loadMoreRowsCallback}
                            rowCount={count}
                        >
                            {({ onRowsRendered, registerChild }) => (
                                <List
                                    ref={(ref) => {
                                        listRef.current = ref;
                                        registerChild(ref);
                                    }}
                                    deferredMeasurementCache={cache.current}
                                    width={
                                        /* istanbul ignore next */ bounds?.width ??
                                        0
                                    }
                                    height={
                                        /* istanbul ignore next */ bounds?.height ??
                                        0
                                    }
                                    rowCount={count}
                                    rowHeight={cache.current.rowHeight}
                                    rowRenderer={rowRenderer}
                                    onRowsRendered={onRowsRendered}
                                    overscanRowCount={10}
                                    noRowsRenderer={() => (
                                        <NoDocs
                                            layout={layout}
                                            requestShowFilter={
                                                requestShowFilter
                                            }
                                        />
                                    )}
                                    rows={rows}
                                    scrollToIndex={scrollToRow}
                                    scrollToAlignment="start"
                                />
                            )}
                        </InfiniteLoader>
                    </ListGroup>
                    {children}
                </div>
            )}
        </Measure>
    );
};

export default DocListSimple;
